import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select, { SingleValue } from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import Button, { ButtonType } from "../../components/Assets/Button";
import { RootDispatch, RootState } from "../../store";
import { IAddress, IStateDistrict } from "../../models/LeadData.model";
import { IProduct } from "../../models/productData.model";
import { IItemOption } from "../../models/quotationData.model";
import { usePreventWheelChange } from "../../utils/usePreventWheelChange";
import { getAllBOMs } from "../../store/slices/bomSlice";
import { fetchItemOptions } from "../../store/slices/quotationSlice";
import toast from "react-hot-toast";
import { IBOM } from "../../models/bom.model";
import { Trash2, Plus, Package, Layers, XCircle } from "lucide-react";
import {
  getUnitsByType,
  getDefaultUnitForType,
} from "../../utils/warehouse.util";
import { calculateOrderTotals } from "../../utils/orderTotals";

interface QuotationModalProps {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  setName: React.Dispatch<React.SetStateAction<string>>;
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  mobileNumber: string;
  setMobileNumber: React.Dispatch<React.SetStateAction<string>>;
  address: IAddress;
  setAddress: React.Dispatch<React.SetStateAction<IAddress>>;
  transportPrice: number;
  discountPrice: number;
  setTransportPrice: React.Dispatch<React.SetStateAction<number>>;
  setDiscountPrice: React.Dispatch<React.SetStateAction<number>>;
  bookedProducts: IProduct[];
  setBookedProducts: React.Dispatch<React.SetStateAction<IProduct[]>>;
  handleCreateQuotation: (
    disableGst: () => void,
    customGst?: number | null,
  ) => Promise<void>;
  statesData: IStateDistrict[];
  isEdit?: boolean;
  handleUpdateQuotation?: (
    disableGst: () => void,
    customGst?: number | null,
  ) => Promise<void>;
}

interface AddressOption {
  value: string;
  label: string;
}

const OptimizedAddQuotationModal: React.FC<QuotationModalProps> = ({
  isOpen,
  onClose,
  name,
  setName,
  email,
  setEmail,
  mobileNumber,
  setMobileNumber,
  address,
  setAddress,
  transportPrice,
  discountPrice,
  setTransportPrice,
  setDiscountPrice,
  bookedProducts,
  setBookedProducts,
  handleCreateQuotation,
  statesData,
  isEdit = false,
  handleUpdateQuotation,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const handleWheelPrevent = usePreventWheelChange();

  const boms = useSelector((state: RootState) => state.billOfMaterials.boms);

  const [selectedBomId, setSelectedBomId] = useState<string>("");
  const [selectedOption, setSelectedOption] =
    useState<SingleValue<IItemOption>>(null);
  const [manualQty, setManualQty] = useState<number>(1);
  const [manualUnit, setManualUnit] = useState<string>("");
  const [manualPrice, setManualPrice] = useState<number>(0);

  const isCreating = useSelector(
    (state: RootState) => state.quotations.isCreating,
  );
  const isUpdating = useSelector(
    (state: RootState) => state.quotations.isUpdating,
  );
  const [initialSnapshot, setInitialSnapshot] = useState<string>("");

  const [enableCustomGST, setEnableCustomGST] = useState(false);
  const [customGST, setCustomGST] = useState<number | null | undefined>();
  const disableGst = () => {
    setEnableCustomGST(false);
    setCustomGST(null);
  };

  useEffect(() => {
    // Capture the baseline only once per open, otherwise it keeps syncing to the latest edits
    if (isOpen && isEdit) {
      setInitialSnapshot((prev) =>
        prev === ""
          ? JSON.stringify({
              transportPrice,
              discountPrice,
              bookedProducts,
            })
          : prev,
      );
    }

    if (!isOpen) {
      setInitialSnapshot("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, isOpen]);

  const onUpdateClick = async () => {
    if (isUpdating) return;
    const currentSnapshot = JSON.stringify({
      transportPrice,
      discountPrice,
      bookedProducts,
    });
    if (currentSnapshot === initialSnapshot) {
      toast.error("No changes detected to update.");
      return;
    }
    if (handleUpdateQuotation)
      await handleUpdateQuotation(disableGst, customGST);
  };

  useEffect(() => {
    if (isOpen) dispatch(getAllBOMs());
  }, [isOpen, dispatch]);

  const totals = useMemo(() => {
    const orderItems = bookedProducts.map((item) => ({
      quantity: item.quantity || 0,
      transportPrice,
      discountPrice,
    }));

    const customerOrders = bookedProducts.map((item) => ({
      price: item.price || 0,
      gst:
        enableCustomGST && customGST !== null && customGST !== undefined
          ? customGST
          : item.gst || 0,
    }));

    return calculateOrderTotals(orderItems as any, customerOrders as any);
  }, [
    bookedProducts,
    transportPrice,
    discountPrice,
    enableCustomGST,
    customGST,
  ]);
  const loadItemOptions = async (
    search: string,
    _loadedOptions: unknown,
    additional?: { page: number },
  ) => {
    const page = additional?.page ?? 1;
    try {
      const response = await dispatch(
        fetchItemOptions({ page, limit: 10, search }),
      ).unwrap();
      const options: IItemOption[] = response.data?.itemOptions || [];
      const totalPages = response.pagination?.totalPages ?? 1;
      return {
        options,
        hasMore: page < totalPages,
        additional: { page: page + 1 },
      };
    } catch {
      return { options: [], hasMore: false, additional: { page } };
    }
  };

  const stateOptions: AddressOption[] = useMemo(
    () => statesData.map((s) => ({ value: s.state, label: s.state })),
    [statesData],
  );

  const cityOptions: AddressOption[] = useMemo(() => {
    const selectedStateData = statesData.find((s) => s.state === address.state);
    return selectedStateData?.city?.map((c) => ({ value: c, label: c })) || [];
  }, [statesData, address.state]);

  useEffect(() => {
    if (selectedOption?.type === "material") {
      const defaultUnit = selectedOption.data.unitType
        ? getDefaultUnitForType(selectedOption.data.unitType)
        : "";
      setManualUnit(defaultUnit || "");
    } else if (selectedOption?.type === "product") {
      setManualUnit(selectedOption.data.unit || "Piece");
    } else {
      setManualUnit("");
    }
    setManualPrice(selectedOption?.data.price || 0);
  }, [selectedOption]);
  if (!isOpen) return null;

  const handleCleanCancel = () => {
    setSelectedBomId("");
    setSelectedOption(null);
    setManualQty(1);
    setManualPrice(0);
    onClose();
  };

  const addItemToBooked = (
    id: string,
    name: string,
    price: number,
    qty: number,
    gst: number,
    type: "product" | "material",
    unit: string,
    unitType?: string,
  ) => {
    setBookedProducts((prev) => {
      const newProducts = [...prev] as IProduct[];
      const existingIndex = newProducts.findIndex(
        (p) => (p._id || p.productName) === id,
      );
      if (existingIndex >= 0) {
        const existing = newProducts[existingIndex];
        newProducts[existingIndex] = {
          ...existing,
          quantity: (existing.quantity || 0) + qty,
          unit: type === "material" ? unit : existing.unit,
        };
      } else {
        newProducts.push({
          _id: id,
          productName: name,
          price: price,
          quantity: qty,
          unit: unit,
          discount: 0,
          gst,
          hsn: 0,
          msp: 0,
          type: type,
          unitType: unitType,
        });
      }
      return newProducts;
    });
  };

  const handleAddManualItem = () => {
    if (!selectedOption) return toast.error("Please select an item");
    if (manualQty <= 0) return toast.error("Quantity must be greater than 0");
    if (selectedOption.type === "material" && !manualUnit)
      return toast.error("Please select a unit");
    const { data, type, value, label } = selectedOption;
    addItemToBooked(
      value,
      label,
      manualPrice || 0,
      manualQty,
      data.gst || 0,
      type,
      manualUnit,
      type === "material" ? data.unitType : undefined,
    );
    setSelectedOption(null);
    setManualQty(1);
    setManualUnit("");
    setManualPrice(0);
    toast.success("Item added");
  };

  const handleAddBOM = (): void => {
    const selectedBOM = boms.find((b: IBOM) => b._id === selectedBomId);
    if (!selectedBOM?.items) return;
    selectedBOM.items.forEach((it) => {
      if (it.type === "product" && it.product) {
        addItemToBooked(
          it.product._id!,
          it.product.productName,
          it.product.price || 0,
          it.quantity,
          it.product.gst || 0,
          "product",
          it.product.unit || "",
        );
      } else if (it.type === "material" && it.rawMaterial) {
        addItemToBooked(
          it.rawMaterial._id,
          it.rawMaterial.name,
          0,
          it.quantity,
          it.rawMaterial?.gst || 0,
          "material",
          it.displayUnit || "",
          it.rawMaterial.unitType,
        );
      }
    });
    toast.success("BOM items added");
    setSelectedBomId("");
  };

  const handleUpdateProduct = (
    index: number,
    field: keyof IProduct,
    value: any,
  ) => {
    setBookedProducts((prev) => {
      const newProducts = [...prev] as IProduct[];
      newProducts[index] = { ...newProducts[index], [field]: value };
      return newProducts;
    });
  };

  const handleRemoveProduct = (index: number) => {
    setBookedProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNav = (e: React.KeyboardEvent, row: number, col: string) => {
    if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
      e.preventDefault();
      const colMap = ["price", "quantity", "discount"];
      let nRow = row;
      let nCol = col;
      if (e.key === "ArrowUp") nRow--;
      if (e.key === "ArrowDown") nRow++;
      if (e.key === "ArrowLeft") {
        const idx = colMap.indexOf(col);
        if (idx > 0) nCol = colMap[idx - 1];
      }
      if (e.key === "ArrowRight") {
        const idx = colMap.indexOf(col);
        if (idx < colMap.length - 1) nCol = colMap[idx + 1];
      }
      const el = document.querySelector(
        `[data-nav-r="${nRow}"][data-nav-c="${nCol}"]`,
      ) as HTMLInputElement;
      if (el) {
        el.focus();
        el.select?.();
      }
    }
  };

  const formatOptionLabel = ({ label, type }: IItemOption) => (
    <div className="flex justify-between items-center">
      <span>{label}</span>
      <span
        className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${type === "product" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}
      >
        {type}
      </span>
    </div>
  );

  const customSelectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: "38px",
      fontSize: "0.875rem",
      borderColor: "#D1D5DB",
      borderRadius: "0.5rem",
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  };

  if (!isOpen) return null;

  const footerRow = bookedProducts.length;

  return (
    <div className="fixed inset-0 p-0 md:p-4 text-xs flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm z-[90]">
      <style>{`
        input::-webkit-outer-spin-button, input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>
      <div className="bg-white rounded-xl md:rounded-2xl shadow-2xl w-full max-w-[1100px] h-[100vh] md:h-[90vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center p-3 md:p-6 border-b border-gray-100 bg-white">
          <h2 className="text-lg md:text-xl font-bold text-gray-800">
            {isEdit ? "Edit Quotation" : "Create Quotation"}
          </h2>
          <button
            onClick={handleCleanCancel}
            className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XCircle size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          <div className="p-3 md:p-6 space-y-4 md:space-y-6">
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 space-y-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    Enable Custom GST
                  </p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Override default GST for this quotation
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  {enableCustomGST && (
                    <div className="relative w-24">
                      <input
                        type="number"
                        value={customGST || 5}
                        onWheel={handleWheelPrevent}
                        onChange={(e) =>
                          setCustomGST(Number(e.target.value) || 0)
                        }
                        className="w-full pl-3 pr-7 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                        min={0}
                      />
                      <span className="absolute right-3 top-2 text-gray-500 text-sm">
                        %
                      </span>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setCustomGST(!enableCustomGST ? 5 : null);
                      setEnableCustomGST((prev) => !prev);
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                      enableCustomGST ? "bg-blue-600" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 ${
                        enableCustomGST ? "translate-x-5" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">
                    Load Template (BOM)
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select
                      value={selectedBomId}
                      onChange={(e) => setSelectedBomId(e.target.value)}
                      className="w-full flex-1 p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a BOM...</option>
                      {boms.map((bom) => (
                        <option key={bom._id} value={bom._id}>
                          {bom.name} ({bom.items?.length ?? 0} items)
                        </option>
                      ))}
                    </select>
                    <Button
                      type={ButtonType.Submit}
                      disabled={!selectedBomId}
                      onClick={handleAddBOM}
                      className="h-[38px] px-4 whitespace-nowrap w-full sm:w-auto"
                    >
                      Load BOM
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-gray-300 text-xs">
                <div className="h-px bg-gray-200 flex-1"></div>
                <span>OR ADD INDIVIDUALLY</span>
                <div className="h-px bg-gray-200 flex-1"></div>
              </div>
              <div>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:items-end">
                  <div className="w-full sm:flex-1 sm:min-w-[180px]">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">
                      Add Item
                    </label>
                    <AsyncPaginate
                      value={selectedOption}
                      onChange={(val) => setSelectedOption(val)}
                      loadOptions={loadItemOptions}
                      additional={{ page: 1 }}
                      debounceTimeout={300}
                      formatOptionLabel={formatOptionLabel}
                      styles={customSelectStyles}
                      placeholder="Search Products or Raw Materials..."
                      isClearable
                      menuPortalTarget={document.body}
                    />
                  </div>
                  <div className="w-[calc(50%-4px)] sm:w-24 shrink-0">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">
                      Price
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={manualPrice}
                      onWheel={handleWheelPrevent}
                      onChange={(e) =>
                        setManualPrice(parseFloat(e.target.value) || 0)
                      }
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 outline-none h-[38px]"
                      placeholder="Price"
                    />
                  </div>
                  <div className="w-[calc(50%-4px)] sm:w-20 shrink-0">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">
                      Qty
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={manualQty}
                      onChange={(e) =>
                        setManualQty(parseFloat(e.target.value) || 0)
                      }
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 outline-none h-[38px]"
                      placeholder="Qty"
                    />
                  </div>
                  {selectedOption?.type === "material" &&
                  selectedOption.data.unitType ? (
                    <div className="w-[calc(50%-4px)] sm:w-24 shrink-0">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">
                        Unit
                      </label>
                      <select
                        value={manualUnit}
                        onChange={(e) => setManualUnit(e.target.value)}
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 outline-none bg-white h-[38px]"
                      >
                        <option value="">Select Unit</option>
                        {getUnitsByType(selectedOption.data.unitType).map(
                          (u) => (
                            <option key={u.value} value={u.value}>
                              {u.label}
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                  ) : (
                    <div className="w-[calc(50%-4px)] sm:w-24 shrink-0">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">
                        Unit
                      </label>
                      <div className="w-full flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200 text-gray-400 text-xs h-[38px]">
                        {selectedOption?.type === "product"
                          ? selectedOption.data.unit || "Piece"
                          : "-"}
                      </div>
                    </div>
                  )}
                  <Button
                    type={ButtonType.Submit}
                    onClick={handleAddManualItem}
                    className="h-[38px] px-4 flex items-center justify-center gap-2 shrink-0 w-full sm:w-auto"
                  >
                    <Plus size={16} /> Add
                  </Button>
                </div>
              </div>
            </div>
            {/* Desktop */}
            <div className="hidden md:block border border-gray-200 rounded-lg shadow-sm bg-white relative overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2.5 whitespace-nowrap text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider first:rounded-tl-lg">
                      Item Details
                    </th>
                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2.5 whitespace-nowrap text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider w-28">
                      Price (₹)
                    </th>
                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2.5 whitespace-nowrap text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider w-20">
                      Qty
                    </th>
                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2.5 whitespace-nowrap text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider w-24">
                      Unit
                    </th>
                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2.5 whitespace-nowrap text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider w-28">
                      Discount (₹)
                    </th>
                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2.5 whitespace-nowrap text-right text-[11px] font-bold text-gray-500 uppercase tracking-wider w-28">
                      Total
                    </th>
                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2.5 whitespace-nowrap text-right text-[11px] font-bold text-gray-500 uppercase tracking-wider w-10 last:rounded-tr-lg"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bookedProducts.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-5 py-12 text-center text-gray-400 italic bg-gray-50/30"
                      >
                        No items added yet.
                      </td>
                    </tr>
                  ) : (
                    bookedProducts.map((item, index) => (
                      <tr
                        key={`${item._id}-${index}`}
                        className="group hover:bg-blue-50/30 transition-colors"
                      >
                        <td className="px-3 py-2 align-middle min-w-[160px]">
                          <div className="font-semibold text-gray-900 text-sm">
                            {item.productName}
                          </div>
                          <div className="mt-1 flex items-center gap-2">
                            {item.type === "material" ? (
                              <span className="flex items-center gap-1 text-[10px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded uppercase">
                                <Layers size={10} /> Material
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded uppercase">
                                <Package size={10} /> Product
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 align-middle w-28">
                          <input
                            type="number"
                            data-nav-r={index}
                            data-nav-c="price"
                            value={item.price}
                            onWheel={handleWheelPrevent}
                            onKeyDown={(e) => handleNav(e, index, "price")}
                            onChange={(e) =>
                              handleUpdateProduct(
                                index,
                                "price",
                                e.target.value === ""
                                  ? 0
                                  : parseFloat(e.target.value),
                              )
                            }
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                          />
                        </td>
                        <td className="px-3 py-2 align-middle w-20">
                          <input
                            type="number"
                            data-nav-r={index}
                            data-nav-c="quantity"
                            value={item.quantity}
                            onWheel={handleWheelPrevent}
                            onKeyDown={(e) => handleNav(e, index, "quantity")}
                            onChange={(e) =>
                              handleUpdateProduct(
                                index,
                                "quantity",
                                e.target.value === ""
                                  ? 0
                                  : parseFloat(e.target.value),
                              )
                            }
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-center"
                          />
                        </td>
                        <td className="px-3 py-2 align-middle w-24">
                          {item.type === "material" &&
                          getUnitsByType(item.unitType!).length > 0 ? (
                            <select
                              value={item.unit || ""}
                              onChange={(e) =>
                                handleUpdateProduct(
                                  index,
                                  "unit",
                                  e.target.value,
                                )
                              }
                              className="w-full px-1.5 py-1.5 border border-gray-300 rounded text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
                            >
                              {getUnitsByType(item.unitType!).map((u) => (
                                <option key={u.value} value={u.value}>
                                  {u.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="py-1.5 px-2 bg-gray-50 rounded text-gray-500 text-xs font-medium border border-gray-200 text-center">
                              {item.unit || "-"}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 align-middle w-28">
                          <input
                            type="number"
                            data-nav-r={index}
                            data-nav-c="discount"
                            value={item.discount || 0}
                            onWheel={handleWheelPrevent}
                            onKeyDown={(e) => handleNav(e, index, "discount")}
                            onChange={(e) =>
                              handleUpdateProduct(
                                index,
                                "discount",
                                e.target.value === ""
                                  ? 0
                                  : Math.max(0, parseFloat(e.target.value)),
                              )
                            }
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                          />
                        </td>
                        <td className="px-3 py-2 align-middle text-right font-bold text-gray-900 text-sm w-28">
                          ₹
                          {Math.max(
                            0,
                            (item.price || 0) * (item.quantity || 0) -
                              (item.discount || 0),
                          ).toFixed(2)}
                        </td>
                        <td className="px-2 py-2 align-middle text-right w-10">
                          <button
                            onClick={() => handleRemoveProduct(index)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Mobile */}
            <div className="md:hidden space-y-3">
              {bookedProducts.length === 0 ? (
                <div className="border rounded-lg p-6 text-center text-gray-400">
                  No items added yet.
                </div>
              ) : (
                bookedProducts.map((item, index) => (
                  <div
                    key={`${item._id}-${index}`}
                    className="border border-gray-200 rounded-xl p-3 bg-white shadow-sm"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-sm">
                          {item.productName}
                        </h3>

                        <span
                          className={`inline-flex mt-1 px-2 py-1 rounded text-[10px] font-bold ${
                            item.type === "material"
                              ? "bg-green-100 text-green-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {item.type}
                        </span>
                      </div>

                      <button
                        onClick={() => handleRemoveProduct(index)}
                        className="p-2 text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] text-gray-500">
                          Price
                        </label>

                        <input
                          type="number"
                          value={item.price}
                          onChange={(e) =>
                            handleUpdateProduct(
                              index,
                              "price",
                              e.target.value === ""
                                ? 0
                                : parseFloat(e.target.value),
                            )
                          }
                          className="w-full mt-1 px-2 py-2 border rounded"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] text-gray-500">Qty</label>

                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) =>
                            handleUpdateProduct(
                              index,
                              "quantity",
                              e.target.value === ""
                                ? 0
                                : parseFloat(e.target.value),
                            )
                          }
                          className="w-full mt-1 px-2 py-2 border rounded"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] text-gray-500">
                          Discount
                        </label>

                        <input
                          type="number"
                          value={item.discount || 0}
                          onChange={(e) =>
                            handleUpdateProduct(
                              index,
                              "discount",
                              e.target.value === ""
                                ? 0
                                : Math.max(0, parseFloat(e.target.value)),
                            )
                          }
                          className="w-full mt-1 px-2 py-2 border rounded"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] text-gray-500">
                          Unit
                        </label>

                        {item.type === "material" &&
                        item.unitType &&
                        getUnitsByType(item.unitType).length > 0 ? (
                          <select
                            value={item.unit || ""}
                            onChange={(e) =>
                              handleUpdateProduct(index, "unit", e.target.value)
                            }
                            className="w-full mt-1 px-2 py-2 border rounded bg-white"
                          >
                            {getUnitsByType(item.unitType).map((u) => (
                              <option key={u.value} value={u.value}>
                                {u.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="mt-1 px-2 py-2 border rounded bg-gray-50">
                            {item.unit || "-"}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 flex justify-between border-t pt-3">
                      <span className="font-medium">Total</span>

                      <span className="font-bold text-blue-600">
                        ₹
                        {Math.max(
                          0,
                          (item.price || 0) * (item.quantity || 0) -
                            (item.discount || 0),
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-gray-700 text-xs font-bold uppercase tracking-wide">
                  Transport Charges
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">₹</span>
                  <input
                    type="number"
                    data-nav-r={footerRow}
                    data-nav-c="price"
                    value={transportPrice || 0}
                    onWheel={handleWheelPrevent}
                    onKeyDown={(e) => handleNav(e, footerRow, "price")}
                    onChange={(e) =>
                      setTransportPrice(Number(e.target.value) || 0)
                    }
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                    min={0}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-gray-700 text-xs font-bold uppercase tracking-wide">
                  Overall Discount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">₹</span>
                  <input
                    type="number"
                    data-nav-r={footerRow}
                    data-nav-c="quantity"
                    value={discountPrice || 0}
                    onWheel={handleWheelPrevent}
                    onKeyDown={(e) => handleNav(e, footerRow, "quantity")}
                    onChange={(e) =>
                      setDiscountPrice(Number(e.target.value) || 0)
                    }
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                    min={0}
                  />
                </div>
              </div>
            </div>
            <hr className="border-gray-100" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 pb-6">
              <div className="space-y-2">
                <label className="text-gray-700 text-xs font-bold uppercase tracking-wide">
                  Customer Name
                </label>
                <input
                  type="text"
                  data-nav-r={footerRow + 1}
                  data-nav-c="price"
                  value={name ?? ""}
                  onKeyDown={(e) => handleNav(e, footerRow + 1, "price")}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isEdit}
                  className={`w-full px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 ${isEdit ? "bg-gray-50 cursor-not-allowed" : "border-gray-300"}`}
                />
              </div>
              <div className="space-y-2">
                <label className="text-gray-700 text-xs font-bold uppercase tracking-wide">
                  Email
                </label>
                <input
                  type="email"
                  data-nav-r={footerRow + 1}
                  data-nav-c="quantity"
                  value={email}
                  onKeyDown={(e) => handleNav(e, footerRow + 1, "quantity")}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isEdit}
                  className={`w-full px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 ${isEdit ? "bg-gray-50 cursor-not-allowed" : "border-gray-300"}`}
                />
              </div>
              <div className="space-y-2">
                <label className="text-gray-700 text-xs font-bold uppercase tracking-wide">
                  Mobile
                </label>
                <input
                  type="text"
                  data-nav-r={footerRow + 2}
                  data-nav-c="price"
                  value={mobileNumber}
                  onKeyDown={(e) => handleNav(e, footerRow + 2, "price")}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  disabled={isEdit}
                  className={`w-full px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 ${isEdit ? "bg-gray-50 cursor-not-allowed" : "border-gray-300"}`}
                />
              </div>
              <div className="space-y-2">
                <label className="text-gray-700 text-xs font-bold uppercase tracking-wide">
                  Street Address
                </label>
                <input
                  type="text"
                  data-nav-r={footerRow + 2}
                  data-nav-c="quantity"
                  value={address.line1}
                  onKeyDown={(e) => handleNav(e, footerRow + 2, "quantity")}
                  onChange={(e) =>
                    setAddress((prev) => ({ ...prev, line1: e.target.value }))
                  }
                  disabled={isEdit}
                  placeholder="Street Address"
                  className={`w-full px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 ${isEdit ? "bg-gray-50 cursor-not-allowed" : "border-gray-300"}`}
                />
              </div>
              <div className="space-y-2">
                <label className="text-gray-700 text-xs font-bold uppercase tracking-wide">
                  State
                </label>
                <Select
                  options={stateOptions}
                  value={
                    stateOptions.find((s) => s.value === address.state) || null
                  }
                  onChange={(opt) =>
                    setAddress((prev) => ({
                      ...prev,
                      state: opt?.value || "",
                      city: "",
                    }))
                  }
                  isDisabled={isEdit}
                  placeholder="Select State"
                  styles={customSelectStyles}
                  isClearable
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="space-y-2">
                <label className="text-gray-700 text-xs font-bold uppercase tracking-wide">
                  City
                </label>
                <Select
                  options={cityOptions}
                  value={
                    cityOptions.find((c) => c.value === address.city) || null
                  }
                  onChange={(opt) =>
                    setAddress((prev) => ({ ...prev, city: opt?.value || "" }))
                  }
                  isDisabled={!address.state || isEdit}
                  placeholder="Select City"
                  styles={customSelectStyles}
                  isClearable
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="space-y-2">
                <label className="text-gray-700 text-xs font-bold uppercase tracking-wide">
                  Pincode
                </label>
                <input
                  type="text"
                  data-nav-r={footerRow + 3}
                  data-nav-c="price"
                  value={address.pincode}
                  onKeyDown={(e) => handleNav(e, footerRow + 3, "price")}
                  onChange={(e) =>
                    setAddress((prev) => ({ ...prev, pincode: e.target.value }))
                  }
                  disabled={isEdit}
                  placeholder="Pincode"
                  className={`w-full px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 ${isEdit ? "bg-gray-50 cursor-not-allowed" : "border-gray-300"}`}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 flex-shrink-0 z-20">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 flex-1">
            {totals.discountPrice > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-[9px] uppercase font-bold tracking-wide text-gray-400">
                  Discount
                </p>
                <p className="text-sm font-bold text-red-500 truncate">
                  -₹{totals.discountPrice.toFixed(2)}
                </p>
              </div>
            )}
            <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
              <p className="text-[9px] uppercase font-bold tracking-wide text-gray-400">
                Sub Total
              </p>
              <p className="text-sm font-bold text-gray-800 truncate">
                ₹{(totals.subTotal - (totals.discountPrice || 0)).toFixed(2)}
              </p>
            </div>

            {totals.freight.transportPrice > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-[9px] uppercase font-bold tracking-wide text-gray-400">
                  Transport
                </p>
                <p className="text-sm font-bold text-sky-600 truncate">
                  ₹{totals.freight.transportPrice.toFixed(2)}
                </p>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
              <p className="text-[9px] uppercase font-bold tracking-wide text-gray-400">
                Tax
              </p>
              <p className="text-sm font-bold text-orange-600 truncate">
                ₹{totals.totalTax.toFixed(2)}
              </p>
            </div>

            <div className="bg-blue-600 rounded-lg px-3 py-2">
              <p className="text-[9px] uppercase font-bold tracking-wide text-blue-100">
                Total
              </p>
              <p className="text-base font-extrabold text-white truncate">
                ₹{totals.grandTotal.toFixed(2)}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-2 shrink-0 w-full xl:w-auto">
            <Button
              type={ButtonType.Cancel}
              onClick={handleCleanCancel}
              disabled={isCreating || isUpdating}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 whitespace-nowrap w-full sm:w-auto"
            >
              Cancel
            </Button>

            {isEdit ? (
              <Button
                type={ButtonType.Submit}
                onClick={onUpdateClick}
                disabled={isUpdating}
                className="px-4 py-2 flex items-center justify-center gap-2 whitespace-nowrap w-full sm:w-auto"
              >
                {isUpdating && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {isUpdating ? "Updating..." : "Update"}
              </Button>
            ) : (
              <Button
                type={ButtonType.Submit}
                onClick={() => handleCreateQuotation(disableGst, customGST)}
                disabled={isCreating}
                className="px-4 py-2 flex items-center justify-center gap-2 whitespace-nowrap w-full sm:w-auto"
              >
                {isCreating && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {isCreating ? "Creating..." : "Create"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OptimizedAddQuotationModal;
